import Pbf from 'pbf';
import { VectorTile } from '@mapbox/vector-tile';
import { boundaryZoom, finestPlaceType, geometryRings } from './placeBoundary.cjs';
import { blurField, distanceTo, morph } from './distanceField.cjs';

// Roads and water from Mapbox Streets vector tiles (OpenStreetMap data), drawn as the cut masks of the
// wooden layers: white = wood stays, black = cut away. Coordinates are Web Mercator "world" units, 0..1.
const TILE_URL = 'https://api.mapbox.com/v4/mapbox.mapbox-streets-v8';
const MAX_TILE_ZOOM = 16;
// Mapbox leaves height off some small buildings; about two storeys
const DEFAULT_BUILDING_M = 6;
export const MASK_WIDTH = 2048;
export const TILE_SIZE = 256;
// Keep preview masks at MASK_WIDTH. 4096² (taller for 30×40) × roads+land, then the copies
// Preview3d makes for burns/alphas, is what kills the rétegelt fa editor on phones while topo
// (768) and fa csillagtérkép (2048, one main ply) keep working.
// zoomed-out views only keep highways, so they stay closer to the cut width
function previewRoadScale(zoom) {
    return 1.1 + (Math.min(16, Math.max(10, zoom)) - 10) * (0.7 / 6);
}
// solid border of the road layer that holds the road pieces together, in mask px
const RIM = 40;

// in mask px by Mapbox road class, the same on every zoom so the cut stays laser-friendly
const ROAD_WIDTHS = {
    motorway: 13,
    trunk: 12,
    primary: 10,
    secondary: 9,
    tertiary: 7,
    motorway_link: 6,
    trunk_link: 6,
    primary_link: 6,
    secondary_link: 6,
    tertiary_link: 5,
    street: 4.5,
    street_limited: 4,
    pedestrian: 4,
    service: 3,
};
// importance by class, links count as their road; service / pedestrian rank below the cut tiers so they're only ever engraved
const ROAD_RANKS = { motorway: 0, trunk: 0, primary: 1, secondary: 2, tertiary: 3, street: 4, street_limited: 4, service: 5, pedestrian: 5 };
// The view's main roads are cut, the next two tiers down are engraved into the land ply, the rest is left out:
// a dense grid of cut strips is too fiddly to make and reads as clutter. Zoomed out, fewer tiers make it.
export function roadStyle(roadClass, zoom) {
    const link = roadClass.endsWith('_link');
    const rank = ROAD_RANKS[link ? roadClass.slice(0, -5) : roadClass];
    if (rank == null) return null;
    const cutRank = zoom < 12 ? 1 : zoom < 14 ? 2 : 3;
    // ramps are fiddly strips, engraved with the road they belong to
    if (rank <= cutRank) return link ? 'engrave' : 'cut';
    return rank <= cutRank + 2 && !link ? 'engrave' : null;
}
// engraved country border width, in mask px
const BORDER_WIDTH = 3;
// rivers stay thicker than the cleanup's minimum gap; canals only once zoomed in, or a delta city looks flooded
const RIVER_WIDTHS = { river: 10, canal: 9 };
const CANAL_MIN_ZOOM = 14;
// laser-safe minimums, in mask px (~0.15 mm on a 30 cm board): water gaps and land strips thinner than
// 2 × MIN_HALF_WIDTH and land islands / water holes smaller than MIN_PART_AREA are smoothed away
const MIN_HALF_WIDTH = 4;
const MIN_PART_AREA = 900;

export function roadWidth(roadClass, zoom) {
    return roadStyle(roadClass, zoom) ? ROAD_WIDTHS[roadClass] || 0 : 0;
}

export { blurField, distanceTo, finestPlaceType };

export function toWorld(lat, lng) {
    const y = Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
    return [(lng + 180) / 360, (1 - y / Math.PI) / 2];
}

export function fromWorld(x, y) {
    return {
        lat: (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI,
        lng: x * 360 - 180,
    };
}

export function viewFromCenter(lat, lng, zoom, width, height) {
    const [cx, cy] = toWorld(lat, lng);
    const worldPerPx = 1 / (TILE_SIZE * 2 ** zoom);
    return {
        left: cx - (width / 2) * worldPerPx,
        top: cy - (height / 2) * worldPerPx,
        right: cx + (width / 2) * worldPerPx,
        bottom: cy + (height / 2) * worldPerPx,
    };
}

export function zoomForView(view, width, height, minZoom, maxZoom) {
    const zoomX = Math.log2(width / (TILE_SIZE * (view.right - view.left)));
    const zoomY = Math.log2(height / (TILE_SIZE * (view.bottom - view.top)));
    return Math.round(Math.min(maxZoom, Math.max(minZoom, Math.min(zoomX, zoomY))));
}

// Same roads/water as the cut, no labels and no rim. Light grey land, stronger water and roads.
// in the plate's own woods: land ply, backplate showing through the water, roads on top
export function drawLocatorPreview(dest, features, view, zoom, woods) {
    const w = dest.width;
    const h = dest.height;
    const ctx = dest.getContext('2d');
    const scaleX = w / (view.right - view.left);
    const scaleY = h / (view.bottom - view.top);
    const widthScale = w / MASK_WIDTH;
    const trace = (rings) =>
        rings.forEach((ring) =>
            ring.forEach(([x, y], index) => {
                const px = (x - view.left) * scaleX;
                const py = (y - view.top) * scaleY;
                if (index) ctx.lineTo(px, py);
                else ctx.moveTo(px, py);
            })
        );

    ctx.fillStyle = woods.middle;
    ctx.fillRect(0, 0, w, h);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.fillStyle = woods.backplate;
    ctx.strokeStyle = woods.backplate;
    features.forEach((feature) => {
        if (feature.river) {
            if (feature.waterway === 'canal' && zoom < CANAL_MIN_ZOOM) return;
            ctx.lineWidth = Math.max(1.5, feature.river * widthScale);
            ctx.beginPath();
            trace(feature.rings);
            ctx.stroke();
        } else if (feature.water) {
            ctx.beginPath();
            trace(feature.rings);
            ctx.fill('evenodd');
        }
    });
    ctx.strokeStyle = woods.top;
    features.forEach((feature) => {
        if (!feature.road) return;
        const width = roadWidth(feature.road, zoom);
        if (!width) return;
        ctx.lineWidth = Math.max(1.2, width * widthScale);
        ctx.beginPath();
        trace(feature.rings);
        ctx.stroke();
    });
}

export async function geocodePlaces(query, token, { language, proximity, types } = {}) {
    const params = new URLSearchParams({ access_token: token, limit: '5', autocomplete: 'true' });
    if (language) params.set('language', language);
    if (types) params.set('types', types);
    if (proximity) params.set('proximity', `${proximity.lng},${proximity.lat}`);
    const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?${params}`
    );
    if (!response.ok) throw new Error(`Mapbox geocoding error ${response.status}`);
    return (await response.json()).features || [];
}

// World-mercator rings of a place outline. Points and lines contribute nothing.
export function boundaryRings(geometry) {
    return geometryRings(geometry).map((ring) => ring.map(([lng, lat]) => toWorld(lat, lng)));
}

// shortcut: Nominatim allows about one lookup a second and may refuse browser traffic, proxy it if outlines start failing
export async function loadPlaceBoundary(feature) {
    if (!feature.center) return [];
    const [lng, lat] = feature.center;
    const placeType = finestPlaceType(feature.place_type);
    const zoom = boundaryZoom(placeType);
    if (!zoom) return [];
    const bbox = feature.bbox;
    const span = bbox ? Math.max(Math.abs(bbox[2] - bbox[0]), Math.abs(bbox[3] - bbox[1])) : 0.4;
    const threshold = Math.min(0.05, Math.max(0.0008, span / 350));
    const params = new URLSearchParams({
        lat: String(lat),
        lon: String(lng),
        format: 'jsonv2',
        polygon_geojson: '1',
        polygon_threshold: String(Math.round(threshold * 1e4) / 1e4),
        zoom: String(zoom),
    });
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params}`);
    if (!response.ok) throw new Error(`Boundary lookup failed ${response.status}`);
    const hit = await response.json();
    return boundaryRings(hit.geojson);
}

export function tilesFor({ left, top, right, bottom }, zoom) {
    const z = Math.min(Math.round(zoom), MAX_TILE_ZOOM);
    const n = 2 ** z;
    const tiles = [];
    for (let x = Math.floor(left * n); x <= Math.floor(right * n); x++) {
        for (let y = Math.floor(top * n); y <= Math.floor(bottom * n); y++) tiles.push({ z, x, y });
    }
    return tiles;
}

function decodeTile(buffer, { z, x, y }) {
    const tile = new VectorTile(new Pbf(buffer));
    const features = [];
    const read = (layerName, pick) => {
        const layer = tile.layers[layerName];
        if (!layer) return;
        const size = layer.extent * 2 ** z;
        for (let i = 0; i < layer.length; i++) {
            const feature = layer.feature(i);
            const item = pick(feature.properties, feature.type);
            if (!item) continue;
            item.rings = feature
                .loadGeometry()
                .map((ring) => ring.map((point) => [(x * layer.extent + point.x) / size, (y * layer.extent + point.y) / size]));
            features.push(item);
        }
    };
    // VectorTileFeature types: 2 = line, 3 = polygon; tunnels aren't visible so aren't cut
    read('road', (p, type) => type === 2 && p.structure !== 'tunnel' && ROAD_WIDTHS[p.class] && { road: p.class });
    read('waterway', (p, type) => type === 2 && RIVER_WIDTHS[p.class] && { river: RIVER_WIDTHS[p.class], waterway: p.class });
    read('water', (p, type) => type === 3 && { water: true });
    // land borders between countries, in the US worldview so disputed areas get one line, not one per worldview
    read(
        'admin',
        (p, type) =>
            type === 2 &&
            p.admin_level === 0 &&
            String(p.maritime) !== 'true' &&
            (p.worldview === 'all' || String(p.worldview).includes('US')) && { border: true }
    );
    // shortcut: decoded for every tile though only the city topo mode uses them, split the cache if wood-map memory suffers
    read('building', (p, type) => type === 3 && p.underground !== 'true' && { building: Number(p.height) || DEFAULT_BUILDING_M });
    return features;
}

// shortcut: tiles stay cached for the whole visit, add an LRU limit if memory becomes a problem
const cache = new Map();

function loadTile(tile, token) {
    const key = `${tile.z}/${tile.x}/${tile.y}`;
    if (!cache.has(key)) {
        const request = fetch(`${TILE_URL}/${key}.vector.pbf?access_token=${token}`)
            .then(async (response) => {
                // no map data there (e.g. open sea)
                if (response.status === 404) return [];
                if (!response.ok) throw new Error(`Mapbox tile error ${response.status}`);
                return decodeTile(await response.arrayBuffer(), tile);
            })
            .catch((error) => {
                cache.delete(key);
                throw error;
            });
        cache.set(key, request);
    }
    return cache.get(key);
}

async function loadAllFeatures(view, zoom, token) {
    if (!token) throw new Error('MAPBOX_TOKEN is not set');
    const tiles = await Promise.all(tilesFor(view, zoom).map((tile) => loadTile(tile, token)));
    return tiles.flat();
}

// roads and water; the mask code treats anything else as water, so buildings stay out
export async function loadMapFeatures(view, zoom, token) {
    return (await loadAllFeatures(view, zoom, token)).filter((feature) => !feature.building);
}

export async function loadBuildings(view, zoom, token) {
    return (await loadAllFeatures(view, zoom, token)).filter((feature) => feature.building);
}

export { morph };

// flips connected areas of `value` smaller than minArea px to the other value
export function dropSmallParts(bits, w, h, value, minArea) {
    const seen = new Uint8Array(bits.length);
    const queue = new Int32Array(bits.length);
    for (let start = 0; start < bits.length; start++) {
        if (seen[start] || bits[start] !== value) continue;
        let head = 0;
        let tail = 0;
        const visit = (j) => {
            if (!seen[j] && bits[j] === value) {
                seen[j] = 1;
                queue[tail++] = j;
            }
        };
        visit(start);
        while (head < tail) {
            const i = queue[head++];
            const x = i % w;
            if (x > 0) visit(i - 1);
            if (x < w - 1) visit(i + 1);
            if (i >= w) visit(i - w);
            if (i < bits.length - w) visit(i + w);
        }
        if (tail < minArea) for (let k = 0; k < tail; k++) bits[queue[k]] = 1 - value;
    }
    return bits;
}

// land = 1: close thin water, open thin land, then drop tiny islands and ponds
export function cleanLandBits(bits, w, h, r = MIN_HALF_WIDTH, minArea = MIN_PART_AREA) {
    let out = morph(morph(bits, w, h, r, true), w, h, r, false);
    out = morph(morph(out, w, h, r, false), w, h, r, true);
    dropSmallParts(out, w, h, 0, minArea);
    return dropSmallParts(out, w, h, 1, minArea);
}

function cleanLandCanvas(canvas) {
    const { width, height } = canvas;
    const ctx = canvas.getContext('2d');
    const image = ctx.getImageData(0, 0, width, height);
    const data = image.data;
    const bits = new Uint8Array(width * height);
    for (let i = 0; i < bits.length; i++) bits[i] = data[i * 4] > 127 ? 1 : 0;
    cleanLandBits(bits, width, height).forEach((bit, i) => {
        data[i * 4] = data[i * 4 + 1] = data[i * 4 + 2] = bit ? 255 : 0;
    });
    ctx.putImageData(image, 0, 0);
}

function maskCanvas(width, height, background) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    return { canvas, ctx };
}

// Returns { roads, land, minorRoads } mask canvases of `view` with the given aspect.
// minorRoads is white on transparent: the preview engraves it as is, the cutting reads its red channel.
export function drawLayerMasks(features, view, zoom, aspect) {
    const width = MASK_WIDTH;
    const height = Math.round(width / aspect);
    const res = 1;
    const scaleX = width / (view.right - view.left);
    const scaleY = height / (view.bottom - view.top);
    const trace = (ctx, rings) =>
        rings.forEach((ring) =>
            ring.forEach(([x, y], index) => {
                const px = (x - view.left) * scaleX;
                const py = (y - view.top) * scaleY;
                if (index) ctx.lineTo(px, py);
                else ctx.moveTo(px, py);
            })
        );

    const roads = maskCanvas(width, height, '#000');
    const land = maskCanvas(width, height, '#fff');
    const minorRoads = maskCanvas(width, height, 'transparent');
    roads.ctx.strokeStyle = '#fff';
    minorRoads.ctx.strokeStyle = '#fff';
    land.ctx.fillStyle = '#000';
    land.ctx.strokeStyle = '#000';

    features.forEach((feature) => {
        if (feature.road) {
            const road = roadWidth(feature.road, zoom);
            if (!road) return;
            const { ctx } = roadStyle(feature.road, zoom) === 'cut' ? roads : minorRoads;
            ctx.lineWidth = road * res * previewRoadScale(zoom);
            ctx.beginPath();
            trace(ctx, feature.rings);
            ctx.stroke();
        } else if (feature.river) {
            if (feature.waterway === 'canal' && zoom < CANAL_MIN_ZOOM) return;
            land.ctx.lineWidth = feature.river * res;
            land.ctx.beginPath();
            trace(land.ctx, feature.rings);
            land.ctx.stroke();
        } else if (feature.border) {
            minorRoads.ctx.lineWidth = BORDER_WIDTH * res;
            minorRoads.ctx.beginPath();
            trace(minorRoads.ctx, feature.rings);
            minorRoads.ctx.stroke();
        } else if (feature.water) {
            // ponds, fish farms, moats: each too small to read, together they look flooded
            const area = feature.rings.reduce(
                (sum, ring) => sum + ring.reduce((a, [x, y], i) => {
                    const [nx, ny] = ring[(i + 1) % ring.length];
                    return a + x * ny - nx * y;
                }, 0),
                0
            );
            if ((Math.abs(area) / 2) * scaleX * scaleY < MIN_PART_AREA) return;
            land.ctx.beginPath();
            trace(land.ctx, feature.rings);
            // evenodd keeps the inner rings (islands) as land
            land.ctx.fill('evenodd');
        }
    });

    cleanLandCanvas(land.canvas);
    roads.ctx.lineWidth = RIM * 2 * res;
    roads.ctx.strokeRect(0, 0, width, height);
    return { roads: roads.canvas, land: land.canvas, minorRoads: minorRoads.canvas };
}
